import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectPortalRedeemStatus } from "../features/portal/portal-slice";
import { redeemPortalToken } from "../features/portal/slice-extensions/portal-slice-extension";
import { ROUTES } from "../routes";

/** Magic-link landing: redeems the token from the URL and enters the portal. */
const PortalAuthPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const status = useAppSelector(selectPortalRedeemStatus);
  const redeemedRef = useRef(false);

  const token = searchParams.get("token");

  useEffect(() => {
    // A magic link is single-use — guard against StrictMode double-invocation.
    if (token && !redeemedRef.current) {
      redeemedRef.current = true;
      dispatch(redeemPortalToken(token))
        .unwrap()
        .then(() => navigate(ROUTES.PORTAL, { replace: true }))
        .catch(() => undefined);
    }
  }, [token, dispatch, navigate]);

  const failed = !token || status === "failed";

  return (
    <section className="mx-auto w-full max-w-md px-4 py-12 text-center">
      {failed ? (
        <>
          <h1 className="mb-2 text-2xl font-bold text-grey-20 dark:text-white">
            {t("portal.linkInvalidTitle", "This link doesn't work anymore")}
          </h1>
          <p className="mb-6 text-sm text-grey-40 dark:text-grey-80">
            {t(
              "portal.linkInvalidBody",
              "Sign-in links work once and expire after 15 minutes. Request a new one and try again.",
            )}
          </p>
          <Link to={ROUTES.PORTAL_LOGIN} className="btn btn-primary inline-block">
            {t("portal.requestNewLink", "Request a new link")}
          </Link>
        </>
      ) : (
        <p className="text-sm text-grey-40 dark:text-grey-80">
          {t("portal.signingIn", "Signing you in…")}
        </p>
      )}
    </section>
  );
};

export default PortalAuthPage;
